import { useState } from 'react';
import { Button, Caption1, Field, Input, MessageBar, MessageBarBody, MessageBarTitle, makeStyles } from '@fluentui/react-components';
import { tokens } from '@vigeo/ui';
import type { PortalLink } from '@/lib/api';

const useStyles = makeStyles({
  reveal: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  row: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'flex-end', flexWrap: 'wrap' },
  grow: { flexGrow: 1, minWidth: '18rem' },
});

/** The link, the code and how long they last, with Copy buttons. Shown once; see `usePortalLinkIssue`. */
export function PortalLinkReveal({ issued }: { readonly issued: PortalLink }) {
  const styles = useStyles();
  const [copied, setCopied] = useState<'link' | 'code' | 'failed' | null>(null);
  const copy = async (text: string, which: 'link' | 'code') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
    } catch {
      setCopied('failed');
    }
  };
  const link = `${window.location.origin}/portal?t=${issued.token}`;
  return (
    <div className={styles.reveal}>
      {issued.revokedPrevious ? (
        <MessageBar intent="warning">
          <MessageBarBody>The previous link for this person stopped working just now.</MessageBarBody>
        </MessageBar>
      ) : null}

      <MessageBar intent="success">
        <MessageBarBody>
          <MessageBarTitle>Copy both now</MessageBarTitle>
          This is the only time these are shown. Closing or reloading this page loses them,
          and the only way to recover is to issue a new link.
        </MessageBarBody>
      </MessageBar>

      <div className={styles.row}>
        <Field label="Link" className={styles.grow}>
          <Input readOnly value={link} onFocus={(e) => e.target.select()} />
        </Field>
        <Button onClick={() => void copy(link, 'link')}>{copied === 'link' ? 'Copied' : 'Copy link'}</Button>
      </div>

      <div className={styles.row}>
        <Field label="Access code" className={styles.grow}>
          <Input readOnly value={issued.accessCode} onFocus={(e) => e.target.select()} />
        </Field>
        <Button onClick={() => void copy(issued.accessCode, 'code')}>{copied === 'code' ? 'Copied' : 'Copy code'}</Button>
      </div>

      {copied === 'failed' ? <Caption1>Could not copy automatically. Select the text and copy it by hand.</Caption1> : null}
      <Caption1>Expires {new Date(issued.expiresAt).toLocaleDateString()}.</Caption1>
    </div>
  );
}
